import { api } from "../../../api/Middleware";
import {
  AGENT_DEPLOYMENTS_LIST_URL,
  AGENT_DEPLOYMENTS_DETAIL_URL,
  AGENT_DEPLOYMENTS_ACTIVATE_URL,
  AGENT_DEPLOYMENTS_SUSPEND_URL,
  AGENT_DEPLOYMENTS_CANCEL_URL,
  AGENT_DEPLOYMENTS_MARK_SETUP_PAID_URL,
  AGENT_DEPLOYMENTS_PROCESS_BILLING_URL,
  AGENT_DEPLOYMENTS_BILLING_SUMMARY_URL,
  AGENT_DEPLOYMENTS_EXTEND_SUPPORT_URL,
  AGENT_DEPLOYMENTS_AVAILABLE_AGENTS_URL,
  AGENT_DEPLOYMENTS_AVAILABLE_AGENT_FLOWS_URL,
  AGENT_DEPLOYMENTS_CREATE_URL,
  AGENT_DEPLOYMENTS_CREATE_FLOW_URL,
} from "../../../api/Urls";

// =============================
// AGENT DEPLOYMENTS SERVICES
// =============================

// Listar todas as implementações de agentes
export const listAgentDeployments = async (params = {}) => {
  return await api.get(AGENT_DEPLOYMENTS_LIST_URL, { params });
};

// Buscar detalhes de implementação específica
export const getAgentDeployment = async (id) => {
  return await api.get(AGENT_DEPLOYMENTS_DETAIL_URL(id));
};

// Buscar agentes disponíveis para implementação
export const getAvailableAgents = async () => {
  return await api.get(AGENT_DEPLOYMENTS_AVAILABLE_AGENTS_URL);
};

// Criar nova implementação de agente
export const createAgentDeployment = async (data) => {
  return await api.post(AGENT_DEPLOYMENTS_CREATE_URL, data);
};

// Buscar AgentFlows (v2) disponíveis para implementação
export const getAvailableAgentFlows = async () => {
  return await api.get(AGENT_DEPLOYMENTS_AVAILABLE_AGENT_FLOWS_URL);
};

// Criar implementação de AgentFlow (v2)
export const createAgentFlowDeployment = async (data) => {
  return await api.post(AGENT_DEPLOYMENTS_CREATE_FLOW_URL, data);
};

// =============================
// AÇÕES DE STATUS
// =============================

// Ativar implementação
export const activateAgentDeployment = async (id) => {
  return await api.post(AGENT_DEPLOYMENTS_ACTIVATE_URL(id));
};

// Suspender implementação
export const suspendAgentDeployment = async (id) => {
  return await api.post(AGENT_DEPLOYMENTS_SUSPEND_URL(id));
};

// Cancelar implementação
export const cancelAgentDeployment = async (id) => {
  return await api.post(AGENT_DEPLOYMENTS_CANCEL_URL(id));
};

// =============================
// AÇÕES FINANCEIRAS
// =============================

// Marcar taxa de setup como paga
export const markSetupFeePaid = async (id) => {
  return await api.post(AGENT_DEPLOYMENTS_MARK_SETUP_PAID_URL(id));
};

// Processar cobrança
export const processBilling = async (id) => {
  return await api.post(AGENT_DEPLOYMENTS_PROCESS_BILLING_URL(id));
};

// Obter resumo de faturamento
export const getBillingSummary = async (id) => {
  return await api.get(AGENT_DEPLOYMENTS_BILLING_SUMMARY_URL(id));
};

// =============================
// AÇÕES DE SUPORTE
// =============================

// Estender suporte
export const extendSupport = async (id, data = {}) => {
  return await api.post(AGENT_DEPLOYMENTS_EXTEND_SUPPORT_URL(id), data);
};

// =============================
// HELPER FUNCTIONS
// =============================

// Construir parâmetros de busca para a API
export const buildSearchParams = (filters) => {
  const params = {};

  // Paginação
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;

  // Busca
  if (filters.search) params.search = filters.search;

  // Filtros
  if (filters.status && filters.status !== "all") params.status = filters.status;
  if (filters.subaccount) params.subaccount = filters.subaccount;
  if (filters.support_type && filters.support_type !== "all") params.support_type = filters.support_type;
  if (filters.setup_fee_paid !== undefined) params.setup_fee_paid = filters.setup_fee_paid;

  // Ordenação
  if (filters.ordering) params.ordering = filters.ordering;

  return params;
};

// Formatar dados da implementação para exibição
export const formatDeploymentData = (deployment) => {
  console.log("🔍 Dados originais da API:", deployment);
  
  const formatted = {
    ...deployment,
    
    // Status formatado
    statusVariant: getStatusVariant(deployment.status),
    statusText: getStatusText(deployment.status),
    
    // Setup fee formatado
    setupFeePaidText: deployment.is_setup_fee_paid ? "Pago" : "Pendente",
    setupFeePaidVariant: deployment.is_setup_fee_paid ? "success" : "warning",
    
    // Suporte formatado
    supportTypeText: getSupportTypeText(deployment.support_type),
    supportTypeVariant: deployment.support_type === "premium" ? "primary" : "secondary",
    
    // Datas formatadas
    formattedDeployedAt: deployment.deployed_at 
      ? new Date(deployment.deployed_at).toLocaleDateString("pt-BR")
      : "—",
    
    // Valores monetários formatados
    formattedSetupFee: formatCurrency(deployment.setup_fee),
    formattedMonthlyFee: formatCurrency(deployment.monthly_fee),
    formattedSupportFee: formatCurrency(deployment.support_fee),
    
    // Mapear dados do agente (da API para o formato esperado pelo componente)
    agent: {
      name: deployment.agent_name,
      area: deployment.agent_job,
      version: "1.0", // Valor padrão se não houver na API
      is_active: true, // Valor padrão se não houver na API
    },
    
    // Mapear dados da subconta (da API para o formato esperado pelo componente)
    subaccount: {
      company_name: deployment.subaccount_name,
      brand_name: deployment.organization_name || "—",
      email: "—", // Se não houver na API
      is_active: true, // Valor padrão se não houver na API
    },
    
    // Usar propriedades corretas para setup fee
    setup_fee_paid: deployment.is_setup_fee_paid,
  };
  
  console.log("✅ Dados formatados:", formatted);
  return formatted;
};

// Obter variante do Bootstrap para status
export const getStatusVariant = (status) => {
  const statusMap = {
    pending: "warning",
    active: "success",
    suspended: "warning", 
    canceled: "danger",
  };
  return statusMap[status] || "secondary";
};

// Obter texto legível para status
export const getStatusText = (status) => {
  const statusMap = {
    pending: "Pendente",
    active: "Ativo",
    suspended: "Suspenso",
    canceled: "Cancelado",
  };
  return statusMap[status] || "Desconhecido";
};

// Obter texto legível para tipo de suporte
export const getSupportTypeText = (supportType) => {
  const supportMap = {
    premium: "Premium",
    basic: "Básico",
  };
  return supportMap[supportType] || "Não definido";
};

// Formatar valor monetário
export const formatCurrency = (value) => {
  if (value == null || value === "") return "—";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

// Verificar se o usuário pode executar uma ação
export const canPerformAction = (action, userContext, deployment) => {
  const { user, isOrganization } = userContext;

  // Admin sempre pode tudo
  if (user?.superuser_level === 1) return true;

  // Organização pode gerenciar suas implementações
  if (isOrganization && user?.superuser_level <= 2) return true;

  // Outras verificações específicas por ação
  switch (action) {
    case "view":
      return user?.superuser_level <= 3;
    case "activate":
    case "suspend":
    case "cancel":
      return user?.superuser_level <= 2;
    case "financial":
      return user?.superuser_level <= 2;
    case "support":
      return user?.superuser_level <= 3;
    default:
      return false;
  }
};

// Validar dados antes de enviar para API
export const validateDeploymentData = (data) => {
  const errors = {};

  if (!data.agent) {
    errors.agent = "Agente é obrigatório";
  }

  if (!data.subaccount) {
    errors.subaccount = "Subconta é obrigatória";
  }

  if (data.setup_fee !== undefined && data.setup_fee < 0) {
    errors.setup_fee = "Taxa de setup não pode ser negativa";
  }

  if (data.monthly_fee !== undefined && data.monthly_fee < 0) {
    errors.monthly_fee = "Mensalidade não pode ser negativa";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};